import { CartItem, ProductType } from 'app/types/product';

export interface CartDiscountCodeConfig {
  code: string;
  percentageDiscount: number;
  // Fechas locales: inicio incluido, fin excluido
  startDate: string;
  endDate: string;
  excludedProductTypes: ProductType[];
}

// Tipos que no son servicios ni tratamientos: los códigos de carrito no les aplican
const NON_TREATMENT_PRODUCT_TYPES: ProductType[] = [
  ProductType.Product,
  ProductType.Voucher,
  ProductType.GiftCard,
  ProductType.GlowBox,
  ProductType.GlowGifts,
  ProductType.Emla,
];

export const CART_DISCOUNT_CODES: CartDiscountCodeConfig[] = [
  {
    code: 'BEAUTYDAYS_038',
    percentageDiscount: 10,
    startDate: '2026-08-01T00:00:00',
    endDate: '2026-09-01T00:00:00',
    excludedProductTypes: NON_TREATMENT_PRODUCT_TYPES,
  },
  {
    code: 'BEAUTYDAYS15',
    percentageDiscount: 15,
    startDate: '2026-09-01T00:00:00',
    endDate: '2026-10-01T00:00:00',
    excludedProductTypes: NON_TREATMENT_PRODUCT_TYPES,
  },
  {
    code: 'BEAUTYDAYS10',
    percentageDiscount: 10,
    startDate: '2026-09-01T00:00:00',
    endDate: '2026-10-01T00:00:00',
    excludedProductTypes: NON_TREATMENT_PRODUCT_TYPES,
  },
];

export const normalizeCartDiscountCode = (code?: string | null): string =>
  (code ?? '').trim().toUpperCase();

export const findCartDiscountCode = (
  code?: string | null
): CartDiscountCodeConfig | undefined => {
  const normalizedCode = normalizeCartDiscountCode(code);

  if (!normalizedCode) return undefined;

  return CART_DISCOUNT_CODES.find(
    discountCode => discountCode.code === normalizedCode
  );
};

export const isCartDiscountCodeActive = (
  config: CartDiscountCodeConfig,
  date: Date = new Date()
): boolean => {
  const time = date.getTime();

  return (
    time >= new Date(config.startDate).getTime() &&
    time < new Date(config.endDate).getTime()
  );
};

export const isProductDiscountableByCode = (
  product: CartItem,
  config: CartDiscountCodeConfig
): boolean => !config.excludedProductTypes.includes(product.type);

const getCartItemCurrentPrice = (product: CartItem): number => {
  const priceWithDiscount = Number(product.priceWithDiscount);

  return Number.isFinite(priceWithDiscount)
    ? priceWithDiscount
    : Number(product.price) || 0;
};

export const getCartDiscountCodeAmount = (
  cart: CartItem[],
  code?: string | null,
  date: Date = new Date()
): number => {
  const config = findCartDiscountCode(code);

  if (!config || !isCartDiscountCodeActive(config, date)) return 0;

  const discountableTotal = (cart ?? [])
    .filter(product => isProductDiscountableByCode(product, config))
    .reduce((acc, product) => acc + getCartItemCurrentPrice(product), 0);

  if (discountableTotal <= 0) return 0;

  return Number(
    ((discountableTotal * config.percentageDiscount) / 100).toFixed(2)
  );
};

const formatValidityDay = (date: Date): string =>
  date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const getCartDiscountCodeValidityText = (
  config: CartDiscountCodeConfig
): string => {
  const lastValidDay = new Date(new Date(config.endDate).getTime() - 1);

  return `del ${formatValidityDay(
    new Date(config.startDate)
  )} al ${formatValidityDay(lastValidDay)}`;
};

export interface CartDiscountCodeValidation {
  isValid: boolean;
  amount: number;
  config?: CartDiscountCodeConfig;
  errorMessage?: string;
}

export const validateCartDiscountCode = (
  code: string,
  cart: CartItem[],
  date: Date = new Date()
): CartDiscountCodeValidation => {
  const config = findCartDiscountCode(code);

  if (!config) {
    return {
      isValid: false,
      amount: 0,
      errorMessage: 'El código introducido no es válido.',
    };
  }

  if (!isCartDiscountCodeActive(config, date)) {
    return {
      isValid: false,
      amount: 0,
      errorMessage: `Este código solo es válido ${getCartDiscountCodeValidityText(
        config
      )}.`,
    };
  }

  const amount = getCartDiscountCodeAmount(cart, code, date);

  if (amount <= 0) {
    return {
      isValid: false,
      amount: 0,
      errorMessage:
        'No hay servicios ni tratamientos en el carrito a los que aplicar el código.',
    };
  }

  return { isValid: true, amount, config };
};
